import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import Dashboard from "./scenes/dashboard";
import RentPage from './pages/Rent';
import SellPage from './pages/Sell';
import InvestPage from './pages/invest';
import JointVenturePage from './pages/jointVenture';
import PropertyIssuesPage from './pages/PropertyIssues';
import AgricultureYearlyContractPage from './pages/AgricultureYearlyContract';
import AgricultureSellPage from './pages/AgricultureSell';
import DocumentsVerificationPage from './pages/DocumentsVerification';
import PropertyListingPage from './pages/PropertyListing';
import RentListingPage from './pages/listing/RentListing';
import SellListingPage from './pages/listing/SellListing';
import InvestListingPage from './pages/listing/InvestListing';
import JointVentureListingPage from './pages/listing/JointVentureListing';
import AgricultureListingPage from './pages/listing/AgricultureListing';
import AgricultureSellListingPage from './pages/listing/AgricultureSellListing';
import EditRentProperty from './pages/listing/EditRentProperty';
import EditSellProperty from './pages/listing/EditSellProperty';
import EditAgricultureProperty from './pages/listing/EditAgricultureProperty';
import EditJointVentureProperty from './pages/listing/EditJointVentureProperty';
import AppointmentPage from './pages/Appointment';
import PropertyQuestionsPage from './pages/PropertyQuestions';
// import AgentsList from './pages/AgentsList';
import { CssBaseline, ThemeProvider, Box, useMediaQuery } from "@mui/material";
import { useMode } from "./theme";
import LoginPage from "./pages/LoginPage";
import { AuthProvider, useAuth } from "./auth/Login";
import { useState } from "react";
import ProtectedRoute from "./components/ProtectedRoute";

function MainApp() {
  const [theme] = useMode();
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);

  // Handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        className="app" 
        sx={{ 
          height: '100vh',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden'
        }}
      >
        {user && (
          <Box sx={{ 
            flexShrink: 0, 
            position: isMobile ? 'fixed' : 'sticky',
            top: 0,
            height: isMobile ? '100vh' : '100vh',
            zIndex: 1000,
            left: 0,
            width: isMobile ? 'auto' : 'auto',
            display: isMobile && !isSidebarOpen ? 'none' : 'block',
            transition: 'all 0.3s ease',
            boxShadow: isMobile ? '2px 0 10px rgba(0,0,0,0.1)' : 'none',
          }}>
            <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
          </Box>
        )}
        <Box 
          component="main" 
          className="content"
          sx={{ 
            flexGrow: 1,
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {user && <Topbar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: isMobile ? 1 : 0 }}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={
                user ? <Navigate to="/dashboard" replace /> : <LoginPage />
              } />
              
              {/* Protected Routes */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              
              {/* Property Leads Routes */}
              <Route path="/rent" element={
                <ProtectedRoute requiredRole="dealer">
                  <RentPage />
                </ProtectedRoute>
              } />
              
              <Route path="/sell" element={
                <ProtectedRoute requiredRole="dealer">
                  <SellPage />
                </ProtectedRoute>
              } />
              
              <Route path="/invest" element={
                <ProtectedRoute requiredRole="dealer">
                  <InvestPage />
                </ProtectedRoute>
              } />
              
              <Route path="/jointventure" element={
                <ProtectedRoute requiredRole="dealer">
                  <JointVenturePage />
                </ProtectedRoute>
              } />
              
              <Route path="/agriculture/yearly-contract" element={
                <ProtectedRoute requiredRole="dealer">
                  <AgricultureYearlyContractPage />
                </ProtectedRoute>
              } />
              
              <Route path="/agriculture/sell" element={
                <ProtectedRoute requiredRole="dealer">
                  <AgricultureSellPage />
                </ProtectedRoute>
              } />
              
              {/* Property Listings Routes */}
              <Route path="/listing/rent" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <RentListingPage />
                </ProtectedRoute>
              } />
              
              <Route path="/edit-rent-property/:id" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <EditRentProperty />
                </ProtectedRoute>
              } />
              
              <Route path="/edit-sell-property/:id" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <EditSellProperty />
                </ProtectedRoute>
              } />
              
              <Route path="/listing/sell" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <SellListingPage />
                </ProtectedRoute>
              } />
              
              <Route path="/listing/invest" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <InvestListingPage />
                </ProtectedRoute>
              } />
              
              <Route path="/listing/jointventure" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <JointVentureListingPage />
                </ProtectedRoute>
              } />
              
              <Route path="/listing/agriculture" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <AgricultureListingPage />
                </ProtectedRoute>
              } />
              
              {/* <Route path="/listing/agriculture-sell" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <AgricultureSellListingPage />
                </ProtectedRoute>
              } /> */}
              
              {/* Admin Routes */}
              <Route path="/documents-verification" element={
                <ProtectedRoute requiredAction="Documents Verification">
                  <DocumentsVerificationPage />
                </ProtectedRoute>
              } />
              
              <Route path="/propertyissues" element={
                <ProtectedRoute requiredAction="Property Issues">
                  <PropertyIssuesPage />
                </ProtectedRoute>
              } />
              
              <Route path="/appointments" element={
                <ProtectedRoute requiredRole="Admin">
                  <AppointmentPage />
                </ProtectedRoute>
              } />
              
              <Route path="/property-questions" element={
                <ProtectedRoute requiredRole="Admin">
                  <PropertyQuestionsPage />
                </ProtectedRoute>
              } />
              
              <Route path="/agents" element={
                <ProtectedRoute requiredAction="Agents">
                  <PropertyIssuesPage /> {/* Replace with AgentsList when implemented */}
                </ProtectedRoute>
              } />
              
              <Route path="/edit-agriculture-property/:id" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <EditAgricultureProperty />
                </ProtectedRoute>
              } />
              
              <Route path="/edit-jointventure-property/:id" element={
                <ProtectedRoute requiredAction="Property Listing">
                  <EditJointVentureProperty />
                </ProtectedRoute>
              } />
              
              {/* Catch-all route - redirect to dashboard if logged in, otherwise to login */}
              <Route path="*" element={
                user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
              } />
            </Routes>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;